import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

export const ItemsPerPageDropdown = ({
    value,
    onChange,
}: {
    value: number;
    onChange: (val: number) => void;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const [openUpward, setOpenUpward] = useState(false);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
    const options = [5, 10, 25, 35, 50, 100];

    const updatePosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const estimatedHeight = options.length * 36; // Approx height
            const spaceBelow = window.innerHeight - rect.bottom;
            const shouldOpenUp = spaceBelow < estimatedHeight + 20;

            setOpenUpward(shouldOpenUp);
            setDropdownPosition({
                top: shouldOpenUp
                    ? rect.top + window.scrollY - 4
                    : rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    useEffect(() => {
        if (isOpen) {
            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
        }
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
                dropdownMenuRef.current && !dropdownMenuRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative inline-block w-16">
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full bg-white text-[#422568] text-[11px] font-black px-2 py-1.5 border border-slate-200 rounded-lg flex items-center justify-between focus:outline-none focus:border-[#422568] focus:ring-4 focus:ring-[#422568]/10 transition-all shadow-sm"
            >
                {value}
                <ChevronDown size={12} className={`ml-1 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#422568]' : ''}`} />
            </button>

            {isOpen && createPortal(
                <div
                    ref={dropdownMenuRef}
                    style={{
                        position: 'absolute',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                        transform: openUpward ? 'translateY(-100%)' : 'none'
                    }}
                    className="z-[9999] bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden py-1.5 animate-in fade-in duration-75"
                >
                    {options.map((option) => (
                        <div
                            key={option}
                            onClick={() => {
                                onChange(option);
                                setIsOpen(false);
                            }}
                            className={`cursor-pointer px-3 py-2 text-[11px] font-black transition-all ${option === value
                                ? 'bg-[#422568] text-white'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-[#422568]'
                                }`}
                        >
                            {option}
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};
